'use client';

import { useState, useEffect, useRef } from 'react';
import { X, Upload, Trash2, Image, Video, Loader as Loader2, CircleCheck as CheckCircle2, ArrowLeft, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

interface MediaFile {
  name: string;
  url: string;
  type: 'image' | 'video';
  size: number;
  created_at: string;
}

interface MediaUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BUCKET_NAME = 'moiuntainview';

const MediaUploadModal = ({ isOpen, onClose }: MediaUploadModalProps): JSX.Element | null => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadFiles();
    }
  }, [isOpen]);

  const loadFiles = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.storage.from(BUCKET_NAME).list('', {
        limit: 200,
        sortBy: { column: 'created_at', order: 'desc' },
      });

      if (error) throw error;

      const mediaFiles: MediaFile[] = (data || [])
        .filter(f => !f.id?.includes('.emptyFolderPlaceholder') && f.name !== '.emptyFolderPlaceholder')
        .map(f => {
          const ext = f.name.split('.').pop()?.toLowerCase() || '';
          const isVideo = ['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext);
          const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(f.name);
          return {
            name: f.name,
            url: urlData.publicUrl,
            type: isVideo ? 'video' : 'image',
            size: f.metadata?.size || 0,
            created_at: f.created_at || '',
          };
        });

      setFiles(mediaFiles);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('ERR_NAME_NOT_RESOLVED')) {
        setErrorMsg('Cannot connect to storage. This will work once deployed to production.');
      } else {
        setErrorMsg(`Failed to load files: ${msg}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploading(true);
    const total = selectedFiles.length;
    let uploaded = 0;

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadProgress(`Uploading ${i + 1} of ${total}: ${file.name}`);

        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const fileName = `${timestamp}_${safeName}`;

        const { error } = await supabase.storage.from(BUCKET_NAME).upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

        if (error) {
          const msg = error?.message || '';
          if (msg.includes('Failed to fetch') || msg.includes('ERR_NAME_NOT_RESOLVED')) {
            setUploadProgress('Cannot connect to storage. Uploads will work once deployed to production.');
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
          }
          console.error(`Failed to upload ${file.name}:`, error);
        } else {
          uploaded++;
        }
      }

      setUploadProgress(`Uploaded ${uploaded} of ${total} files`);
      await loadFiles();

      setTimeout(() => setUploadProgress(''), 3000);
    } catch (err) {
      console.error('Upload error:', err);
      setUploadProgress('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (fileName: string) => {
    try {
      const { error } = await supabase.storage.from(BUCKET_NAME).remove([fileName]);
      if (error) throw error;
      setFiles(prev => prev.filter(f => f.name !== fileName));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] p-5 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-xl font-bold">Media Manager</h2>
              <p className="text-white/80 text-sm">{files.length} files in storage</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleUpload}
              className="hidden"
              id="media-upload-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-white text-[#7C8F75] hover:bg-white/90 font-semibold"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Upload Files</>
              )}
            </Button>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {uploadProgress && (
          <div className="px-5 py-3 bg-[#7C8F75]/10 border-b border-[#7C8F75]/20 flex items-center space-x-2">
            <CheckCircle2 className="h-4 w-4 text-[#7C8F75]" />
            <span className="text-sm text-[#333333]">{uploadProgress}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {errorMsg && (
            <div className="mb-4 flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">{errorMsg}</p>
                <p className="text-xs mt-1 text-amber-600">The upload feature works correctly on your deployed site.</p>
              </div>
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#7C8F75]" />
            </div>
          ) : files.length === 0 && !errorMsg ? (
            <div className="flex flex-col items-center justify-center py-20 text-[#333333]/60">
              <Image className="h-16 w-16 mb-4 opacity-40" />
              <p className="text-lg font-medium">No media files yet</p>
              <p className="text-sm mt-1">Upload images and videos to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {files.map((file) => (
                <div key={file.name} className="group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-gray-100">
                  <div className="aspect-square">
                    {file.type === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <video
                          src={file.url}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-black/50 rounded-full p-3">
                            <Video className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white text-xs font-medium truncate mb-1">{file.name}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-white/70 text-xs">{formatFileSize(file.size)}</span>
                        {deleteConfirm === file.name ? (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleDelete(file.name)}
                              className="bg-red-500 text-white text-xs px-2 py-1 rounded hover:bg-red-600"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="bg-gray-500 text-white text-xs px-2 py-1 rounded hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(file.name)}
                            className="text-white/80 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      file.type === 'video'
                        ? 'bg-blue-500/80 text-white'
                        : 'bg-[#7C8F75]/80 text-white'
                    }`}>
                      {file.type === 'video' ? 'VIDEO' : 'IMAGE'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaUploadModal;
